import React, { useCallback, useEffect, useMemo } from 'react';
import { FormControl, Select, useColorMode } from '@chakra-ui/react';

import { useCalendarSettings, useFilters } from '../../../../lib/context';
import { useAutoContrastColor } from '../../../../lib';

interface ISelectFilterComponentProps {
	placeholder: string;
	options: string[];
	selectedOptions: string[];
	filterKey: string;
	color?: string;
	disabled?: boolean;
}

export const SelectFilterComponent: React.FC<ISelectFilterComponentProps> = ({
	color,
	placeholder,
	options,
	selectedOptions,
	filterKey,
	disabled = false,
}) => {
	const { filtersInitializedRef, setFilters } = useFilters();
	const { colorMode } = useColorMode();
	const { settings } = useCalendarSettings();
	const { color: contrastColor, ref } = useAutoContrastColor();

	const handleChange = useCallback(
		(event: React.ChangeEvent<HTMLSelectElement>) => {
			const selectedValues = Array.from(event.target.selectedOptions, (option) => option.value).filter(
				(value) => value !== ''
			);
			setFilters((prev) => ({
				...prev,
				[filterKey]: selectedValues,
			}));
		},
		[filterKey, setFilters]
	);

	useEffect(() => {
		if (options.length === 0 && !filtersInitializedRef) {
			setFilters((prev) => ({
				...prev,
				[filterKey]: [],
			}));
		}
	}, [options, filterKey, filtersInitializedRef, setFilters]);

	const selectSx = useMemo(
		() => ({
			'::placeholder': {
				color: color,
			},
		}),
		[color]
	);

	const optionStyle = useMemo(() => ({ color }), [color]);

	const { calendarBordersColor } = settings.styles[colorMode];
	return (
		<FormControl>
			<Select
				ref={ref}
				size='sm'
				placeholder={placeholder}
				value={selectedOptions}
				onChange={handleChange}
				borderRadius='xs'
				color={color}
				iconColor={contrastColor}
				disabled={disabled}
				sx={selectSx}
				borderColor={calendarBordersColor}
			>
				{options.map((option) => (
					<option key={option} value={option} style={optionStyle}>
						{option}
					</option>
				))}
			</Select>
		</FormControl>
	);
};
